// src/components/ui/XhelosTooltip/XhelosTooltip.jsx

import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom'; // ✅ PASO 1: Importar createPortal
import {
  useFloating,
  useHover,
  useClick,
  useDismiss,
  useInteractions,
  arrow,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/react';
import './XhelosTooltip.css';

export default function XhelosTooltip({
  content,
  children,
  placement = 'top',
  theme = 'dark',
  hasArrow = true,
  interactive = false,
  trigger = 'hover',
  visible = false,
  onVisibleChange = () => {},
}) {
  const arrowRef = useRef(null);

  // Internal state for hover and click tooltips
  const [internalVisible, setInternalVisible] = useState(false);

  // Use internal state for hover/click, external visible prop for manual control
  const isOpen = trigger === 'manual' ? visible : internalVisible;

  // Handle visibility state changes
  const handleOpenChange = (open) => {
    if (trigger !== 'manual') {
      setInternalVisible(open);
    }
    onVisibleChange(open);
  };

  const { x, y, strategy, refs, context, middlewareData } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip(),
      shift({ padding: 8 }),
      hasArrow && arrow({ element: arrowRef }),
    ],
  });

  const click = useClick(context, { enabled: trigger !== 'hover' });
  const hover = useHover(context, {
    enabled: trigger === 'hover',
    handleClose: interactive ? null : undefined,
  });
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
  ]);

  const { x: arrowX, y: arrowY } = middlewareData.arrow || {};
  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.split('-')[0]];

  // El JSX a renderizar en el portal
  const tooltipJsx = isOpen && (
    <div
      ref={refs.setFloating}
      className={`xhelos-tooltip theme-${theme}`}
      data-open={isOpen} // ✅ PASO 2: Añadir el atributo data-open
      role="tooltip"
      {...getFloatingProps()}
      style={{
        position: strategy,
        top: y ?? 0,
        left: x ?? 0,
        zIndex: 9999,
      }}
    >
      {content}
      {hasArrow && (
        <div
          ref={arrowRef}
          className="tooltip-arrow"
          style={{
            left: arrowX != null ? `${arrowX}px` : '',
            top: arrowY != null ? `${arrowY}px` : '',
            [staticSide]: '-4px',
          }}
        />
      )}
    </div>
  );

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()} className="tooltip-trigger">
        {children}
      </div>

      {/* ✅ PASO 3: Usar el portal para renderizar el tooltip en el body */}
      {createPortal(tooltipJsx, document.body)}
    </>
  );
}